import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { shallow } from "zustand/shallow";
import Breadcrumb from "../../components/Breadcrumb";
import { Role, useAuthStore } from "../../stores/auth";
import { useSettingsStore } from "../../stores/settings";
import { useToastStore } from "../../stores/toast";

const DEFAULT_LABELS = {
  delivery_notes_plural: "Formulare",
  delivery_notes_singular: "Formular",
  templates_plural: "Vorlagen",
  templates_singular: "Vorlage",
};

function toFormValues(data) {
  return {
    delivery_notes_plural: data?.delivery_notes?.delivery_notes || "",
    delivery_notes_singular: data?.delivery_notes?.delivery_note || "",
    templates_plural: data?.delivery_notes?.templates?.templates || "",
    templates_singular: data?.delivery_notes?.templates?.template || "",
  };
}

function toPayload(values) {
  const dn = {};
  if (values.delivery_notes_plural) dn.delivery_notes = values.delivery_notes_plural;
  if (values.delivery_notes_singular) dn.delivery_note = values.delivery_notes_singular;

  const tpl = {};
  if (values.templates_plural) tpl.templates = values.templates_plural;
  if (values.templates_singular) tpl.template = values.templates_singular;
  if (Object.keys(tpl).length > 0) dn.templates = tpl;

  if (Object.keys(dn).length === 0) return {};
  return { delivery_notes: dn };
}

function TranslationSettings() {
  const { t } = useTranslation();
  const [hasRole] = useAuthStore((state) => [state.hasRole], shallow);
  const addToast = useToastStore((state) => state.addToast);

  const [translation, fetchTranslation, updateTranslation] = useSettingsStore(
    (s) => [s.translation, s.fetchTranslation, s.updateTranslation],
    shallow,
  );

  const [saving, setSaving] = useState(false);

  const formik = useFormik({
    initialValues: {
      delivery_notes_plural: "",
      delivery_notes_singular: "",
      templates_plural: "",
      templates_singular: "",
    },
    onSubmit: () => {},
  });

  useEffect(() => {
    fetchTranslation().then(({ data }) => {
      if (data) {
        formik.resetForm({ values: toFormValues(data) });
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = toPayload(formik.values);
      const { error } = await updateTranslation(payload);
      if (error) {
        addToast("error", t("errors.unknown"));
        return;
      }
      formik.resetForm({ values: formik.values });
      addToast("success", t("common.saved"));
    } finally {
      setSaving(false);
    }
  };

  if (!hasRole(Role.ADMINISTRATOR)) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <Breadcrumb
        items={[
          { label: t("menu.settings"), to: "/settings" },
          { label: t("settings.translations.title") },
        ]}
      />
      <div className="row">
        <div className="col-lg-6">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">{t("settings.translations.title")}</h5>
              <p className="text-muted">{t("settings.translations.description")}</p>

              <div className="mb-3">
                <label className="form-label">
                  {t("settings.translations.delivery_notes_plural")}
                </label>
                <input
                  type="text"
                  name="delivery_notes_plural"
                  className="form-control"
                  value={formik.values.delivery_notes_plural}
                  onChange={formik.handleChange}
                  placeholder={DEFAULT_LABELS.delivery_notes_plural}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">
                  {t("settings.translations.delivery_notes_singular")}
                </label>
                <input
                  type="text"
                  name="delivery_notes_singular"
                  className="form-control"
                  value={formik.values.delivery_notes_singular}
                  onChange={formik.handleChange}
                  placeholder={DEFAULT_LABELS.delivery_notes_singular}
                />
              </div>

              <hr />

              <div className="mb-3">
                <label className="form-label">
                  {t("settings.translations.templates_plural")}
                </label>
                <input
                  type="text"
                  name="templates_plural"
                  className="form-control"
                  value={formik.values.templates_plural}
                  onChange={formik.handleChange}
                  placeholder={DEFAULT_LABELS.templates_plural}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">
                  {t("settings.translations.templates_singular")}
                </label>
                <input
                  type="text"
                  name="templates_singular"
                  className="form-control"
                  value={formik.values.templates_singular}
                  onChange={formik.handleChange}
                  placeholder={DEFAULT_LABELS.templates_singular}
                />
              </div>

              <button
                type="button"
                className="btn btn-success"
                disabled={saving}
                onClick={handleSave}
              >
                {saving && <i className="fa fa-spinner fa-spin me-1" />}
                {t("common.save")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default TranslationSettings;
